import React, { useCallback, useEffect, useState, useRef } from "react";
import { getClientHeader, getClientFooter } from "../../utils/API_SERVICE";
import { fetchStudyVideos } from "../../utils/videoService";
import { useAuth } from "../../Hooks/useAuth";
import { useStudyId } from "../../Hooks/useStudyId";
import logo from "../../assets/icons/logo.png";
import { exportElementToPaginatedPdf } from "../../utils/previewPdfExport";
import useTemplateProcessor from "../patient/hooks/useTemplateProcessor";
import { usePatientAuthFlags } from "../../Hooks/usePatientAuthFlags";
import { useTranslation } from "react-i18next";
import { useI18nLanguage } from "../../i18n/hooks/useI18nLanguage";
import { getPreviewContentFromReport } from "../../utils/contentProcessor";

const PreviewModal = ({
  isVisible,
  onClose,
  studyData,
  editorRef,
  transcript = "",
}) => {
  const { t } = useTranslation('common');
  const [headerContent, setHeaderContent] = useState("");
  const [footerContent, setFooterContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [reportBodyHtml, setReportBodyHtml] = useState("");
  const [lastVideo, setLastVideo] = useState(null);
  const [introVideo, setIntroVideo] = useState(null);
  const accessToken = useAuth();
  const studyId = useStudyId();
  const { isCreAuth } = usePatientAuthFlags();
  const modalRef = useRef();
  const { isRTL } = useI18nLanguage();

  // Fetch header and footer data when the modal is visible
  useEffect(() => {
    const fetchHeaderAndFooter = async () => {
      try {
        const headerResponse = await getClientHeader({});
        const footerResponse = await getClientFooter({});
        if (headerResponse.success) {
          setHeaderContent(headerResponse.payload.content || "");
        } else {
          throw new Error(t('failedToFetchHeaderContent'));
        }
        if (footerResponse.success) {
          setFooterContent(footerResponse.payload.content || "");
        } else {
          throw new Error(t('failedToFetchFooterContent'));
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    if (isVisible) {
      fetchHeaderAndFooter();
    }
  }, [isVisible, accessToken, t]);

  // Fetch last recorded video and intro video when modal is visible
  useEffect(() => {
    const fetchVideos = async () => {
      if (!isVisible || !studyId || !accessToken) {
        setLastVideo(null);
        setIntroVideo(null);
        return;
      }
      try {
        const response = await fetchStudyVideos(studyId, {
          useCreApi: isCreAuth,
          pageIndex: 0,
          pageSize: 50,
        });
        if (response.success && response.payload) {
          const recordedVideos = response.payload
            .filter((video) => !video.isIntro)
            .sort((a, b) => {
              const dateA = new Date(a.createdAt || 0);
              const dateB = new Date(b.createdAt || 0);
              return dateB - dateA;
            });

          if (recordedVideos.length > 0) {
            const latestVideo = recordedVideos[0];
            setLastVideo({
              url: latestVideo.longUrl,
              vid: latestVideo.vid,
              name: `Video ${latestVideo.vid}`,
            });
          } else {
            setLastVideo(null);
          }

          const intro = response.payload.find((video) => video.isIntro);
          if (intro && intro.longUrl) {
            setIntroVideo({
              url: intro.longUrl,
              vid: intro.vid,
              name: `Intro Video ${intro.vid}`,
            });
          } else {
            setIntroVideo(null);
          }
        }
      } catch (error) {
        console.error("Error fetching videos:", error);
        setLastVideo(null);
        setIntroVideo(null);
      }
    };

    if (isVisible) {
      fetchVideos();
    }
  }, [isVisible, studyId, accessToken, isCreAuth]);

  // Use the template processor hook for header/footer
  const { processedHeader, processedFooter } = useTemplateProcessor(
    studyData,
    headerContent,
    footerContent
  );

  const resolveReportBodyHtml = useCallback(
    () =>
      getPreviewContentFromReport({
        editor: editorRef?.current?.editor,
        transcript,
      }),
    [editorRef, transcript],
  );

  // Snapshot live editor HTML when preview opens or transcript syncs while open.
  useEffect(() => {
    if (!isVisible) {
      setReportBodyHtml("");
      return;
    }
    setReportBodyHtml(resolveReportBodyHtml());
  }, [isVisible, resolveReportBodyHtml]);

  // Close the modal when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, onClose]);

  const handleDownloadPdf = async () => {
    const printContent = document.getElementById("print-content");
    if (!printContent || pdfDownloading) return;

    const freshHtml = resolveReportBodyHtml();
    const bodySlot = printContent.querySelector("[data-preview-report-body]");
    if (bodySlot && freshHtml) {
      bodySlot.innerHTML = freshHtml;
    }

    setPdfDownloading(true);
    try {
      await exportElementToPaginatedPdf(printContent, "report.pdf");
    } catch (err) {
      console.error("PDF download failed:", err);
    } finally {
      setPdfDownloading(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 sm:p-4">
      {/* Responsive modal that adapts to viewport and zoom level */}
      <div
        ref={modalRef}
        className="bg-white w-full max-w-[297mm] h-full sm:h-[90vh] max-h-[100vh] p-3 sm:p-4 md:p-5 overflow-auto rounded-lg shadow-md relative mobile-scroll"
      >
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-2 mb-4 sticky top-0 bg-white z-10 pb-2 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('preview')}</h2>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              className="flex-1 sm:flex-none px-4 py-2.5 sm:p-3 bg-green rounded-3xl text-gray-900 hover:bg-green-dark transition-colors text-sm sm:text-base font-medium min-h-[44px] sm:min-h-0 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={handleDownloadPdf}
              disabled={pdfDownloading}
            >
              {pdfDownloading && (
                <span
                  className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
                  aria-hidden
                />
              )}
              {pdfDownloading ? t('downloading') || "Downloading…" : t('downloadToPdf')}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 sm:py-1 sm:px-3 text-red-500 hover:text-red-700 font-bold rounded transition duration-300 text-sm sm:text-base min-h-[44px] sm:min-h-0"
            >
              {t('close')}
            </button>
          </div>
        </div>
        <div 
          id="print-content" 
          className="prose max-w-none text-sm sm:text-base report-no-hyphens"
        >
          <style>{`
            #print-content,
            #print-content .preview-report-body,
            #print-content .preview-report-body * {
              hyphens: none !important;
              -webkit-hyphens: none !important;
              word-break: normal !important;
              overflow-wrap: normal !important;
            }
            #print-content img {
              display: block !important;
              object-fit: contain !important;
              max-width: 100% !important;
            }
            #print-content img[data-align="left"],
            #print-content img:not([data-align]) {
              margin: 0.5rem 0 0.5rem 0 !important;
              margin-right: auto !important;
            }
            #print-content img[data-align="center"] {
              margin: 0.5rem auto !important;
            }
            #print-content img[data-align="right"] {
              margin: 0.5rem 0 0.5rem 0 !important;
              margin-left: auto !important;
            }
            #print-content table {
              width: 100% !important;
              border-collapse: collapse !important;
              overflow-x: auto !important;
              display: block !important;
            }
            #print-content table thead,
            #print-content table tbody {
              display: table !important;
              width: 100% !important;
            }
            #print-content .preview-report-body ul,
            #print-content .preview-report-body ol {
              margin: 0.5rem 0;
              padding-left: 1.5rem;
            }
            #print-content .preview-report-body ul[data-type="taskList"] {
              list-style: none;
              padding-left: 0;
            }
            #print-content .preview-report-body table {
              width: 100%;
              border-collapse: collapse;
            }
            #print-content .preview-report-body th,
            #print-content .preview-report-body td {
              border: 1px solid #d1d5db;
              padding: 0.375rem 0.5rem;
            }
            @media (max-width: 640px) {
              #print-content {
                font-size: 0.875rem !important;
              }
              #print-content p, #print-content div {
                font-size: 0.875rem !important;
                line-height: 1.5 !important;
              }
            }
          `}</style>
          {loading ? (
            <p className="text-gray-900">{t('loading')}</p>
          ) : error ? (
            <p className="text-red-500">{t('error')}: {error}</p>
          ) : (
            <>
              {processedHeader && (
                <div>
                  {!isCreAuth && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0 mb-4">
                      <img src={logo} alt="RadsForVets" className="h-6 sm:h-8" />
                      <div className="text-left sm:text-right text-xs sm:text-sm text-gray-900">
                        <a
                          href="mailto:support@radsforvets.com"
                          className="text-blue-600 hover:underline block sm:inline"
                        >
                          support@radsforvets.com
                        </a>
                        <div className="text-xs sm:text-sm">1-888-303-RADS (7237)</div>
                      </div>
                    </div>
                  )}
                  <div className="text-center text-lg sm:text-xl md:text-2xl font-bold my-4 flex items-center justify-center gap-2 sm:gap-4">
                    <span className="border-t-2 border-gray-300 flex-1 sm:w-24"></span>
                    <span className="text-gray-900 px-2">{t('finalReport')}</span>
                    <span className="border-t-2 border-gray-300 flex-1 sm:w-24"></span>
                  </div>
                  {lastVideo && lastVideo.url && (
                    <div className="text-center mb-2">
                      <a
                        id="preview-video-link"
                        href={lastVideo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline font-medium text-xs sm:text-sm px-3 py-2 rounded-lg bg-green/20 hover:bg-green/30 transition-colors min-h-[44px] flex items-center justify-center"
                        style={{ color: "#A4C639" }}
                        title={lastVideo.url}
                      >
                        ▶ {t('watchRecording')}
                      </a>
                    </div>
                  )}
                  {introVideo && introVideo.url && (
                    <div className="text-center mb-4">
                      <a
                        id="preview-intro-video-link"
                        href={introVideo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline font-medium text-xs sm:text-sm px-3 py-2 rounded-lg bg-green/20 hover:bg-green/30 transition-colors min-h-[44px] flex items-center justify-center"
                        style={{ color: "#A4C639" }}
                        title={introVideo.url}
                      >
                        ▶ {t('watchIntroVideo')}
                      </a>
                    </div>
                  )}
                  <div
                    className="bg-blue-50 p-3 sm:p-4 rounded-lg grid grid-cols-1 gap-3 sm:gap-4 text-xs sm:text-sm text-gray-900 overflow-x-auto"
                    style={{ whiteSpace: "pre-wrap" }}
                    dir={isRTL ? "rtl" : "ltr"}
                  >
                    <style>{`
                      table { 
                        width: 100% !important; 
                        border-collapse: collapse !important;
                        direction: ${isRTL ? "rtl" : "ltr"} !important;
                      }
                      th, td { 
                        width: auto !important; 
                        text-align: ${isRTL ? "right" : "left"} !important;
                        direction: ${isRTL ? "rtl" : "ltr"} !important;
                        padding: 0.5rem !important;
                        word-break: normal !important;
                        overflow-wrap: normal !important;
                        hyphens: none !important;
                      }
                      @media (max-width: 640px) {
                        table { font-size: 0.75rem !important; }
                        th, td { padding: 0.375rem !important; }
                      }
                    `}</style>
                    <div
                      style={{ width: "100%", minWidth: 0, direction: isRTL ? "rtl" : "ltr" }}
                      dangerouslySetInnerHTML={{ __html: processedHeader }}
                    />
                  </div>
                </div>
              )}
              <hr className="mt-4 border-gray-300" />
              <div
                data-preview-report-body
                className="text-gray-900 text-sm sm:text-base overflow-x-auto preview-report-body report-no-hyphens"
                dir={isRTL ? "rtl" : "ltr"}
                style={{
                  textAlign: isRTL ? "right" : "left",
                  direction: isRTL ? "rtl" : "ltr",
                }}
                dangerouslySetInnerHTML={{ __html: reportBodyHtml }}
              />
              <hr className="border-gray-300 my-4" />
              {processedFooter && (
                <div
                  className="mx-auto overflow-x-auto"
                  style={{ whiteSpace: "pre-wrap" }}
                >
                  <style>{`
                    .preview-content, .preview-content * { 
                      color: #111827 !important; 
                      font-size: 0.875rem !important;
                    }
                    @media (min-width: 640px) {
                      .preview-content, .preview-content * { 
                        font-size: 1rem !important;
                      }
                    }
                  `}</style>
                  <div
                    className="text-gray-900 text-sm sm:text-base"
                    dangerouslySetInnerHTML={{ __html: processedFooter }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
